import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Reveal, MaskLine } from "@/components/site/Reveal";
import Marquee from "@/components/site/Marquee";
import PackTicker from "@/components/site/PackTicker";
import ExportMap from "@/components/site/ExportMap";
import CTABand from "@/components/site/CTABand";
import { BISCUITS, MACHINERY, EXPORT_FACTS } from "@/data/content";

const RotatingStamp = () => (
  <div className="relative h-28 w-28 md:h-36 md:w-36 shrink-0" aria-hidden="true">
    <svg viewBox="0 0 120 120" className="h-full w-full animate-spinSlow text-brand-cream">
      <defs>
        <path id="stamp-circle" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" />
      </defs>
      <text style={{ fontSize: "10px", letterSpacing: "2.6px", fontFamily: "'Space Mono', monospace" }} className="fill-current">
        <textPath href="#stamp-circle">EST. 1961 • NBS NATIONAL •</textPath>
      </text>
    </svg>
    <span className="absolute inset-0 m-auto h-3.5 w-3.5 rounded-full bg-brand-gold" />
  </div>
);

const HomePage = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <main data-testid="page-home">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-brand-choc">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <img src="images/hero_line.jpeg" alt="Biscuit production line at the National factory" className="h-[115%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-choc via-brand-choc/45 to-brand-choc/20" />
        </motion.div>

        <motion.div style={{ opacity: contentOpacity }} className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-end px-5 pb-14 sm:px-8 md:pb-20 lg:px-12">
          <div className="flex items-end justify-between gap-6">
            <div className="max-w-4xl">
              <MaskLine>
                <span data-testid="hero-logo" className="mb-6 hidden md:inline-flex items-center rounded-xl bg-white/90 px-3 py-1.5 shadow-[0_0_30px_rgba(255,255,255,0.4)] ring-1 ring-white/70">
                  <img src="images/nbs_logo_v2.png" alt="NBS National — Baking Since 1961" className="h-16 w-auto md:h-20" />
                </span>
              </MaskLine>
              <MaskLine>
                <p className="mb-5 inline-block rounded-full bg-brand-choc/60 px-5 py-2.5 text-[11px] font-extrabold uppercase tracking-[0.28em] text-brand-cream shadow-[0_2px_18px_rgba(0,0,0,0.35)] backdrop-blur-sm">
                  National Biscuit &amp; Sweet Manufacturers · India
                </p>
              </MaskLine>
              <h1 className="font-display font-light text-brand-cream leading-[0.98] text-[13vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem] tracking-tight">
                <MaskLine delay={0.1}>Baking since</MaskLine>
                <MaskLine delay={0.28}>
                  <span className="italic text-brand-gold">1961.</span>
                </MaskLine>
              </h1>
              <MaskLine delay={0.5}>
                <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-brand-cream/85">
                  From a single hand oven in a small room in India to supermarket shelves
                  across Africa &amp; the Gulf — three generations of one family, one promise.
                </p>
              </MaskLine>
              <MaskLine delay={0.65}>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <Link
                    to="/products"
                    data-testid="hero-cta-products"
                    className="group inline-flex items-center gap-3 rounded-full bg-brand-red px-8 py-4 text-sm font-bold text-brand-cream transition-colors duration-300 hover:bg-brand-cream hover:text-brand-choc"
                  >
                    Explore our products
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid="hero-cta-contact"
                    className="rounded-full border border-brand-cream/40 px-8 py-4 text-sm font-bold text-brand-cream transition-colors duration-300 hover:bg-brand-cream/10"
                  >
                    Talk to the export desk
                  </Link>
                </div>
              </MaskLine>
            </div>
            <div className="hidden md:block pb-6">
              <RotatingStamp />
            </div>
          </div>
        </motion.div>

        <div className="absolute bottom-6 right-6 z-10 hidden text-brand-cream/70 md:flex flex-col items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] [writing-mode:vertical-lr]">Scroll</span>
          <ArrowDown className="h-4 w-4 animate-bounce" />
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee
        items={[
          "Baking since 1961",
          "Exporting since 1998",
          "Third-generation family enterprise",
          "Direct & merchant exports",
          "Africa · Gulf · India",
        ]}
      />

      {/* STORY BENTO */}
      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7 flex flex-col justify-between">
            <Reveal>
              <p className="eyebrow">Our story</p>
              <h2 className="mt-5 font-display font-light text-4xl md:text-6xl leading-[1.05] tracking-tight">
                Two brothers. One oven.
                <br />
                <span className="italic text-brand-red">Sixty years of goodwill.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-brand-muted">
                In 1961, Seth Papumal (Parsram) and Seth Narimal Nagpal started baking biscuits in a single
                small room with a hand oven. They were among the first in the region to package biscuits —
                sealed, dated and trusted — and grew into one of the most renowned names in the industry by
                keeping it that way: the finest raw materials, honest weight, value-driven business.
              </p>
              <Link to="/about" data-testid="home-story-link" className="link-underline mt-6 inline-block text-sm font-bold text-brand-red">
                Read the full story
              </Link>
            </Reveal>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 border-t border-brand-line">
              {EXPORT_FACTS.slice(0, 4).map((f, i) => (
                <Reveal key={f.k} delay={i * 0.08} className="border-b md:border-b-0 border-brand-line py-6 pr-4 md:border-r md:last:border-r-0">
                  <p className="font-mono text-2xl md:text-3xl text-brand-red">{f.k}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-muted">{f.v}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className="relative">
              <img
                src="images/heritage_1961.jpeg"
                alt="The founders baking with a hand oven in 1961"
                className="aspect-[4/5] w-full rounded-2xl object-cover"
              />
              <div className="absolute -bottom-6 -left-6 hidden md:block rounded-xl bg-brand-choc px-7 py-5 text-brand-cream shadow-2xl">
                <p className="font-display italic text-3xl text-brand-gold">1961</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-cream/70">The first bake</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRODUCT SHOWCASE */}
      <section className="border-y border-brand-line bg-brand-sand/60">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <p className="eyebrow">The shelf</p>
              <h2 className="mt-5 font-display font-light text-4xl md:text-6xl tracking-tight">
                Real packs. <span className="italic">Real cravings.</span>
              </h2>
            </div>
            <Link to="/products" data-testid="home-products-link" className="link-underline text-sm font-bold text-brand-red">
              View the full range
            </Link>
          </Reveal>
          <div className="mt-14" data-testid="home-pack-row">
            <PackTicker packs={BISCUITS} onPick={(id) => navigate(`/products?p=${id}`)} />
          </div>
        </div>
      </section>

      {/* GLOBAL REACH */}
      <section className="noise-dark bg-brand-choc text-brand-cream">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
          <Reveal>
            <p className="eyebrow-gold">Biscuit industry · Global</p>
            <h2 className="mt-5 max-w-3xl font-display font-light text-4xl md:text-6xl leading-[1.05] tracking-tight">
              From India to <span className="italic text-brand-gold">major ports</span> across Africa &amp; the Gulf.
            </h2>
          </Reveal>
          <div className="mt-14">
            <ExportMap />
          </div>
        </div>
      </section>

      {/* INFRASTRUCTURE */}
      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
        <Reveal className="max-w-3xl">
          <p className="eyebrow">The modern plant</p>
          <h2 className="mt-5 font-display font-light text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Bright floors. Clean steel. <span className="italic text-brand-red">State-of-the-art machinery.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="machinery-grid">
          {MACHINERY.map((m, i) => (
            <Reveal key={m.no} delay={(i % 3) * 0.08}>
              <div className="group overflow-hidden rounded-2xl border border-brand-line bg-white">
                <div className="h-56 overflow-hidden">
                  <img src={m.img} alt={m.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                    <h3 className="mt-2 font-display text-xl">{m.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-muted">{m.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </main>
  );
};

export default HomePage;
